import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useOrganizationId } from '../hooks/useOrganizationId'
import { useOrganization } from '../contexts/OrganizationContext'
import { useMasterAdmin } from '../contexts/MasterAdminContext'
import Card from '../components/Card'
import Button from '../components/Button'
import Input from '../components/Input'
import Select from '../components/Select'
import {
  Shield,
  Lock,
  Key,
  Globe,
  Database,
  AlertTriangle,
  CheckCircle2,
  X,
  Plus,
  Trash2,
  Download,
  Eye,
  EyeOff,
  Clock,
  Server,
} from 'lucide-react'
import { get2FAStatus, enable2FA, disable2FA, verifyAndEnable2FA } from '../services/twoFactorAuth'
import { getActiveSessions, revokeSession, revokeAllOtherSessions } from '../services/sessionManagement'
import { getIPWhitelist, addIPToWhitelist, removeIPFromWhitelist, toggleIPWhitelistStatus } from '../services/ipWhitelist'
import { getDisasterRecoveryConfig } from '../services/backup'
import { getAuditLogs, getAccessLogs } from '../services/auditLog'
import { formatDate } from '../utils/helpers'

const SecuritySettings = () => {
  const [activeTab, setActiveTab] = useState('overview')
  const [twoFAStatus, setTwoFAStatus] = useState(null)
  const [sessions, setSessions] = useState([])
  const [ipWhitelist, setIPWhitelist] = useState([])
  const [drConfig, setDRConfig] = useState(null)
  const [auditLogs, setAuditLogs] = useState([])
  const [loading, setLoading] = useState(true)
  const [newIP, setNewIP] = useState({ address: '', description: '' })
  const [show2FASetup, setShow2FASetup] = useState(false)
  const [twoFACode, setTwoFACode] = useState('')
  const organizationId = useOrganizationId()
  const { isAdmin, subscription } = useOrganization()
  const { isMasterAdmin } = useMasterAdmin()

  // Only master admins can access this page
  if (!isMasterAdmin) {
    return (
      <div className="section-container">
        <Card variant="soft" className="p-6 text-center">
          <AlertTriangle size={48} className="mx-auto text-red-500 mb-4" />
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Access Denied</h2>
          <p className="text-gray-600">This page is only accessible to master administrators.</p>
        </Card>
      </div>
    )
  }

  useEffect(() => {
    if (organizationId) {
      fetchAllData()
    }
  }, [organizationId])

  const fetchAllData = async () => {
    setLoading(true)
    try {
      const [twoFA, activeSessions, ipList, dr, logs] = await Promise.all([
        get2FAStatus(),
        getActiveSessions(),
        getIPWhitelist(organizationId),
        getDisasterRecoveryConfig(organizationId),
        getAuditLogs(organizationId, { limit: 50 }),
      ])
      setTwoFAStatus(twoFA)
      setSessions(activeSessions)
      setIPWhitelist(ipList)
      setDRConfig(dr)
      setAuditLogs(logs)
    } catch (error) {
      console.error('Error fetching security data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleEnable2FA = async () => {
    try {
      await enable2FA('totp')
      setShow2FASetup(true)
    } catch (error) {
      alert(error.message || 'Failed to enable 2FA')
    }
  }

  const handleVerify2FA = async () => {
    try {
      await verifyAndEnable2FA(twoFACode)
      setShow2FASetup(false)
      setTwoFACode('')
      fetchAllData()
      alert('2FA enabled successfully!')
    } catch (error) {
      alert(error.message || 'Invalid code')
    }
  }

  const handleDisable2FA = async () => {
    if (!confirm('Are you sure you want to disable 2FA? This will reduce your account security.')) return
    try {
      await disable2FA()
      fetchAllData()
      alert('2FA disabled')
    } catch (error) {
      alert(error.message || 'Failed to disable 2FA')
    }
  }

  const handleAddIP = async () => {
    if (!newIP.address.trim()) return
    try {
      await addIPToWhitelist(organizationId, newIP.address, newIP.description)
      setNewIP({ address: '', description: '' })
      fetchAllData()
    } catch (error) {
      alert(error.message || 'Failed to add IP')
    }
  }

  const handleRemoveIP = async (ipId) => {
    if (!confirm('Remove this IP from whitelist?')) return
    try {
      await removeIPFromWhitelist(organizationId, ipId)
      fetchAllData()
    } catch (error) {
      alert(error.message || 'Failed to remove IP')
    }
  }

  const handleRevokeSession = async (sessionId) => {
    try {
      await revokeSession(sessionId)
      fetchAllData()
    } catch (error) {
      alert(error.message || 'Failed to revoke session')
    }
  }

  const handleRevokeAllSessions = async () => {
    if (!confirm('Revoke all other sessions? You will need to log in again on other devices.')) return
    try {
      await revokeAllOtherSessions()
      fetchAllData()
    } catch (error) {
      alert(error.message || 'Failed to revoke sessions')
    }
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Shield },
    { id: '2fa', label: 'Two-Factor Auth', icon: Key },
    { id: 'sessions', label: 'Sessions', icon: Clock },
    { id: 'ip-whitelist', label: 'IP Whitelist', icon: Globe, adminOnly: true },
    { id: 'backups', label: 'Backups', icon: Database, adminOnly: true },
    { id: 'disaster-recovery', label: 'Disaster Recovery', icon: Server, adminOnly: true },
    { id: 'audit-logs', label: 'Audit Logs', icon: AlertTriangle, adminOnly: true },
  ]

  if (loading) {
    return (
      <div className="section-container">
        <p className="text-gray-500">Loading security settings...</p>
      </div>
    )
  }

  return (
    <div className="section-container space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg">
          <Shield size={24} className="text-white" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Security & Compliance</h1>
          <p className="text-gray-600">Manage security settings and compliance features</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 overflow-x-auto">
        {tabs.map((tab) => {
          if (tab.adminOnly && !isAdmin) return null
          const Icon = tab.icon
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-b-2 border-primary-600 text-primary-700'
                  : 'text-gray-600 hover:text-primary-600 hover:border-primary-300'
              }`}
            >
              <Icon size={18} />
              {tab.label}
            </button>
          )
        })}
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
        >
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card variant="soft" className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Two-Factor Authentication</h3>
                  {twoFAStatus?.is_enabled ? (
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                      Enabled
                    </span>
                  ) : (
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                      Disabled
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  {twoFAStatus?.is_enabled
                    ? 'Your account is protected with two-factor authentication.'
                    : 'Add an extra layer of security to your account.'}
                </p>
                <Button
                  variant={twoFAStatus?.is_enabled ? 'secondary' : 'primary'}
                  onClick={twoFAStatus?.is_enabled ? handleDisable2FA : handleEnable2FA}
                  size="sm"
                >
                  {twoFAStatus?.is_enabled ? 'Disable 2FA' : 'Enable 2FA'}
                </Button>
              </Card>

              <Card variant="soft" className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Active Sessions</h3>
                  <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                    {sessions.length} active
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Manage your active login sessions across devices.
                </p>
                <Button variant="secondary" onClick={() => setActiveTab('sessions')} size="sm">
                  Manage Sessions
                </Button>
              </Card>

              <Card variant="soft" className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">IP Whitelist</h3>
                  <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                    {ipWhitelist.filter((ip) => ip.is_active).length} active
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Restrict access to specific IP addresses.
                </p>
                <Button variant="secondary" onClick={() => setActiveTab('ip-whitelist')} size="sm">
                  Manage IPs
                </Button>
              </Card>
            </div>
          )}

          {activeTab === '2fa' && (
            <Card variant="soft" className="p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Two-Factor Authentication</h3>
              {twoFAStatus?.is_enabled ? (
                <div className="space-y-4">
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle2 size={20} className="text-green-600" />
                      <span className="font-semibold text-green-900">2FA is Enabled</span>
                    </div>
                    <p className="text-sm text-green-800">
                      Method: {twoFAStatus.method?.toUpperCase() || 'TOTP'}
                    </p>
                    {twoFAStatus.last_used_at && (
                      <p className="text-xs text-green-700 mt-1">
                        Last used: {formatDate(twoFAStatus.last_used_at)}
                      </p>
                    )}
                  </div>
                  <Button variant="secondary" onClick={handleDisable2FA}>
                    Disable 2FA
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle size={20} className="text-yellow-600" />
                      <span className="font-semibold text-yellow-900">2FA is Disabled</span>
                    </div>
                    <p className="text-sm text-yellow-800">
                      Enable two-factor authentication to add an extra layer of security to your account.
                    </p>
                  </div>
                  <Button onClick={handleEnable2FA}>Enable 2FA</Button>

                  {show2FASetup && (
                    <Card variant="soft" className="p-4 mt-4">
                      <h4 className="font-semibold text-gray-900 mb-3">Verify Setup</h4>
                      <Input
                        label="Enter verification code"
                        type="text"
                        value={twoFACode}
                        onChange={(e) => setTwoFACode(e.target.value)}
                        placeholder="000000"
                        maxLength={6}
                      />
                      <div className="flex gap-3 mt-4">
                        <Button onClick={handleVerify2FA} disabled={twoFACode.length !== 6}>
                          Verify & Enable
                        </Button>
                        <Button variant="secondary" onClick={() => setShow2FASetup(false)}>
                          Cancel
                        </Button>
                      </div>
                    </Card>
                  )}
                </div>
              )}
            </Card>
          )}

          {activeTab === 'sessions' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-gray-900">Active Sessions</h3>
                {sessions.length > 1 && (
                  <Button variant="secondary" size="sm" onClick={handleRevokeAllSessions}>
                    Revoke All Other Sessions
                  </Button>
                )}
              </div>
              {sessions.length === 0 ? (
                <Card variant="soft" className="p-6 text-center">
                  <p className="text-gray-500">No active sessions</p>
                </Card>
              ) : (
                <div className="space-y-3">
                  {sessions.map((session) => (
                    <Card key={session.id} variant="soft" className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="font-semibold text-gray-900">
                              {session.device_info?.name || 'Unknown Device'}
                            </span>
                            {session.is_active && (
                              <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                                Active
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600">
                            IP: {session.ip_address || 'Unknown'} • Last activity:{' '}
                            {formatDate(session.last_activity_at)}
                          </p>
                          {session.user_agent && (
                            <p className="text-xs text-gray-500 mt-1">{session.user_agent}</p>
                          )}
                        </div>
                        {session.is_active && (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => handleRevokeSession(session.id)}
                          >
                            Revoke
                          </Button>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'ip-whitelist' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-gray-900">IP Whitelist</h3>
              </div>
              <Card variant="soft" className="p-4">
                <h4 className="font-semibold text-gray-900 mb-3">Add IP Address</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Input
                    label="IP Address or Range (CIDR)"
                    value={newIP.address}
                    onChange={(e) => setNewIP({ ...newIP, address: e.target.value })}
                    placeholder="e.g., 192.168.1.1 or 192.168.1.0/24"
                  />
                  <Input
                    label="Description"
                    value={newIP.description}
                    onChange={(e) => setNewIP({ ...newIP, description: e.target.value })}
                    placeholder="e.g., Office Network"
                  />
                </div>
                <Button onClick={handleAddIP} className="mt-3" disabled={!newIP.address.trim()}>
                  <Plus size={16} className="mr-1" />
                  Add IP
                </Button>
              </Card>
              {ipWhitelist.length === 0 ? (
                <Card variant="soft" className="p-6 text-center">
                  <p className="text-gray-500">No IP addresses whitelisted</p>
                </Card>
              ) : (
                <div className="space-y-3">
                  {ipWhitelist.map((ip) => (
                    <Card key={ip.id} variant="soft" className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-gray-900">{ip.ip_address}</span>
                            <span
                              className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                                ip.is_active
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-gray-100 text-gray-700'
                              }`}
                            >
                              {ip.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                          {ip.description && (
                            <p className="text-sm text-gray-600">{ip.description}</p>
                          )}
                          <p className="text-xs text-gray-500 mt-1">
                            Added: {formatDate(ip.created_at)}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() =>
                              toggleIPWhitelistStatus(organizationId, ip.id, !ip.is_active).then(
                                fetchAllData
                              )
                            }
                          >
                            {ip.is_active ? 'Deactivate' : 'Activate'}
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => handleRemoveIP(ip.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'disaster-recovery' && (
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-gray-900">Disaster Recovery</h3>
              {drConfig ? (
                <Card variant="soft" className="p-6">
                  <h4 className="font-semibold text-gray-900 mb-4">Recovery Configuration</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Recovery Time Objective (RTO)</p>
                      <p className="font-semibold text-gray-900">{drConfig.recovery_time_objective} hours</p>
                      <p className="text-xs text-gray-500 mt-1">
                        Maximum acceptable downtime
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Recovery Point Objective (RPO)</p>
                      <p className="font-semibold text-gray-900">{drConfig.recovery_point_objective} hours</p>
                      <p className="text-xs text-gray-500 mt-1">
                        Maximum acceptable data loss
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Replication</p>
                      <p className="font-semibold text-gray-900">
                        {drConfig.replication_enabled ? 'Enabled' : 'Disabled'}
                      </p>
                      {drConfig.replication_region && (
                        <p className="text-xs text-gray-500 mt-1">
                          Region: {drConfig.replication_region}
                        </p>
                      )}
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Failover</p>
                      <p className="font-semibold text-gray-900">
                        {drConfig.failover_enabled ? 'Enabled' : 'Disabled'}
                      </p>
                    </div>
                    {drConfig.last_test_at && (
                      <div>
                        <p className="text-sm text-gray-600">Last Test</p>
                        <p className="font-semibold text-gray-900">
                          {formatDate(drConfig.last_test_at)}
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-xs text-blue-800">
                      <strong>Subscription Tier:</strong> {drConfig.subscription_tier} • Disaster
                      recovery is configured automatically based on your subscription tier.
                    </p>
                  </div>
                </Card>
              ) : (
                <Card variant="soft" className="p-6 text-center">
                  <p className="text-gray-500">Disaster recovery configuration not available</p>
                </Card>
              )}
            </div>
          )}

          {activeTab === 'audit-logs' && (
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-gray-900">Audit Logs</h3>
              {auditLogs.length === 0 ? (
                <Card variant="soft" className="p-6 text-center">
                  <p className="text-gray-500">No audit logs found</p>
                </Card>
              ) : (
                <div className="space-y-2">
                  {auditLogs.map((log) => (
                    <Card key={log.id} variant="soft" className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-gray-900 capitalize">
                              {log.action_type}
                            </span>
                            <span className="text-sm text-gray-600">{log.resource_type}</span>
                            {log.user && (
                              <span className="text-xs text-gray-500">by {log.user.email}</span>
                            )}
                          </div>
                          {log.details && Object.keys(log.details).length > 0 && (
                            <p className="text-xs text-gray-600 mt-1">
                              {JSON.stringify(log.details)}
                            </p>
                          )}
                          <p className="text-xs text-gray-500 mt-1">
                            {formatDate(log.created_at)}
                          </p>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  )
}

export default SecuritySettings

